import { ref, watch, onUnmounted } from 'vue';
import { getMarketPageWithUsers } from '@/api/market';
import { normalizeMarketSearchResponse } from '@/utils/search-map';

/**
 * 首页/顶栏搜索：GET /liyuan-market/market/page-with-users
 * @param {import('vue').Ref<string>|import('vue').ComputedRef<string>} keywordRef
 * @param {{ debounceMs?: number, pageNum?: number, pageSize?: number, paginated?: boolean, enabled?: import('vue').Ref<boolean>|import('vue').ComputedRef<boolean> }} [options]
 */
export function useMarketSearch(keywordRef, options = {}) {
  const markets = ref([]);
  const users = ref([]);
  const total = ref(0);
  const loading = ref(false);
  const loadingMore = ref(false);
  const hasMore = ref(false);
  const error = ref(null);

  let reqToken = 0;
  let debounceTimer = null;
  const debounceMs = options.debounceMs ?? 300;
  const pageSize = options.pageSize ?? 6;
  const paginated = options.paginated ?? false;
  const fixedPageNum = options.pageNum ?? 1;

  const currentPage = ref(1);

  async function fetchPage(keyword, pageNum, { append = false } = {}) {
    const q = String(keyword ?? '').trim();
    if (!q) {
      markets.value = [];
      users.value = [];
      total.value = 0;
      hasMore.value = false;
      currentPage.value = 1;
      loading.value = false;
      loadingMore.value = false;
      error.value = null;
      return;
    }

    const token = ++reqToken;
    if (append) {
      loadingMore.value = true;
    } else {
      loading.value = true;
    }
    error.value = null;

    try {
      const data = await getMarketPageWithUsers(q, { pageNum, pageSize });
      if (token !== reqToken) return;

      const startIndex = append ? markets.value.length : 0;
      const parsed = normalizeMarketSearchResponse(data, startIndex);

      if (append) {
        markets.value = [...markets.value, ...parsed.markets];
      } else {
        markets.value = parsed.markets;
        users.value = parsed.users;
      }

      total.value = parsed.total;
      currentPage.value = pageNum;
      hasMore.value = markets.value.length < total.value;
    } catch (err) {
      if (token !== reqToken) return;
      if (!append) {
        markets.value = [];
        users.value = [];
        total.value = 0;
      }
      hasMore.value = false;
      error.value = err;
      console.warn('[search] getMarketPageWithUsers failed', err);
    } finally {
      if (token === reqToken) {
        loading.value = false;
        loadingMore.value = false;
      }
    }
  }

  async function fetchSearch(keyword, append = false) {
    if (paginated) {
      const nextPage = append ? currentPage.value + 1 : 1;
      await fetchPage(keyword, nextPage, { append });
      return;
    }
    await fetchPage(keyword, fixedPageNum, { append: false });
  }

  async function loadMore() {
    if (!paginated || loading.value || loadingMore.value || !hasMore.value) return;
    await fetchSearch(keywordRef.value, true);
  }

  function scheduleFetch(keyword) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      fetchSearch(keyword, false);
    }, debounceMs);
  }

  watch(
    () => [keywordRef.value, options.enabled?.value ?? true],
    ([keyword, enabled]) => {
      if (!enabled) return;
      scheduleFetch(keyword);
    },
    { immediate: true }
  );

  onUnmounted(() => {
    clearTimeout(debounceTimer);
    reqToken += 1;
  });

  return {
    markets,
    users,
    total,
    loading,
    loadingMore,
    hasMore,
    error,
    fetchSearch,
    loadMore,
  };
}
